import { decode } from 'he';
import PropTypes from 'prop-types';
import { useNavigate } from 'react-router-dom';
import styled, { css } from 'styled-components';

import { Box } from '@hivebrite/design-system/Box';
import { Dotdotdot } from '@hivebrite/design-system/Dotdotdot';
import { Link } from '@hivebrite/design-system/Link';
import { Play } from '@hivebrite/design-system/Play';

import VideoPlayer from 'hb-react/frontoffice/components/VideoPlayer';
import {
  isFileAnImageOrHasAPreview,
  isFileAVideo,
  isFileExternalSource,
  isFileAnAudio,
  isFileAnArticle,
} from 'hb-react/shared/utils/files/fileContentTypes';

import ExternalSourceThumbnail from './ExternalSourceThumbnail';
import WrapperContent from './WrapperContent';

const FileThumbnailContent = ({ file, fileUrl, openPlayVideo, to = {} }) => {
  const navigate = useNavigate();
  const {
    url,
    name,
    content_type: contentType,
    file_type: fileType,
    extension,
    uuid,
  } = file;

  const thumbnailUrl = file.new_thumbnail_url || file.thumbnail_url;

  if (isFileAnImageOrHasAPreview(thumbnailUrl, contentType, extension)) {
    return (
      <Link
        to={{
          pathname: fileUrl,
          ...to,
        }}
        aria-label={s__(
          'Accessibility|Click to see the preview of the file %{filename}',
        ).i__({
          filename: name,
        })}
      >
        <WrapperContent
          justifyContent="center"
          data-testid="thumbnail-link-to-preview"
        >
          <StyledImg alt={name} source={thumbnailUrl} />
        </WrapperContent>
      </Link>
    );
  }

  if (isFileAnArticle(fileType)) {
    const decodedName = decode(name);

    return (
      <Link
        to={Routes.show_article_path(uuid)}
        aria-label={s__(
          'Accessibility|Click to see the article %{articleName}',
        ).i__({
          articleName: decodedName,
        })}
      >
        <WrapperContent justifyContent="center">
          <StyledImg alt={decodedName} source={thumbnailUrl} />
        </WrapperContent>
      </Link>
    );
  }

  if (isFileExternalSource(fileType)) {
    return (
      <ExternalSourceThumbnail
        url={url}
        openPlayVideo={openPlayVideo}
        text={name}
        thumbnailUrl={thumbnailUrl}
        to={{
          pathname: fileUrl,
          ...to,
        }}
      />
    );
  }

  if (isFileAVideo(fileType) || isFileAnAudio(fileType)) {
    const handleButtonPlayClick = () =>
      navigate(to?.pathname ?? fileUrl, {
        state: {
          ...to?.state,
          play: true,
        },
      });

    return (
      <Link
        to={{
          pathname: fileUrl,
          ...to,
          state: {
            ...to?.state,
            play: true,
          },
        }}
        aria-label={s__(
          'Accessibility|Click to see the preview of the video %{videoName}',
        ).i__({
          videoName: name,
        })}
      >
        <WrapperContent>
          <WrapperPlay>
            <Play
              ariaLabel={__('Start the video preview')}
              onClick={handleButtonPlayClick}
            />
          </WrapperPlay>
          <VideoDimmer />
          <VideoPlayer url={url} />
        </WrapperContent>
      </Link>
    );
  }

  return (
    <Link
      to={{
        pathname: fileUrl,
        ...to,
      }}
      aria-label={s__(
        'Accessibility|Click to see the preview of the file %{filename}',
      ).i__({
        filename: name,
      })}
    >
      <WrapperContent
        alignItems="center"
        justifyContent="center"
        font="h2-regular"
        textAlign="center"
        p={6}
      >
        <Dotdotdot clamp={2}>{name}</Dotdotdot>
      </WrapperContent>
    </Link>
  );
};

FileThumbnailContent.propTypes = {
  file: PropTypes.shape({
    url: PropTypes.string,
    name: PropTypes.string,
    content_type: PropTypes.string,
    file_type: PropTypes.string,
    thumbnail_url: PropTypes.string,
    extension: PropTypes.string,
  }).isRequired,
  fileUrl: PropTypes.string.isRequired,
  openPlayVideo: PropTypes.func,
  to: PropTypes.object,
};

export default FileThumbnailContent;

const WrapperPlay = styled(Box).attrs(() => ({
  position: 'absolute',
  top: '50%',
  right: '50%',
}))`
  z-index: 2;
  transform: translate(50%, -50%);
`;

const VideoDimmer = styled(Box)`
  background-color: black;
  height: 100%;
  left: 0;
  opacity: 0.3;
  position: absolute;
  top: 0;
  width: 100%;
  z-index: 1;
`;

const StyledImg = styled(Box).withConfig({
  shouldForwardProp: (prop) => !['source'].includes(prop),
})`
  cursor: pointer;
  width: 100%;
  ${({ source }) =>
    source &&
    css`
      background-image: url('${source}');
      background-repeat: no-repeat;
      background-size: cover;
      background-position: center;
    `}
`;
